import { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSPrimaryButton from "views/system-design/DSPrimaryButton";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import useStudioContract from "../hooks/useStudioContract";
import useSaveStudioContract from "../hooks/useSaveStudioContract";

const StudioContractEditor = () => {
  const { data, isPending: isLoading, error } = useStudioContract();
  const { mutate: save, isPending: isSaving } = useSaveStudioContract();
  const [html, setHtml] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (data?.html) {
      setHtml(data.html);
      setHasChanges(false);
    }
  }, [data?.html]);

  const handleChange = (value: string) => {
    setHtml(value);
    setHasChanges(true);
  };

  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSave = () => {
    setSaveError(null);
    save(html, {
      onSuccess: () => setHasChanges(false),
      onError: () => setSaveError("Failed to save contract. Please try again."),
    });
  };

  if (isLoading) {
    return (
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 4,
        }}
      >
        <DSCircularProgress />
      </DSBox>
    );
  }

  if (error) {
    return (
      <DSBox sx={{ p: 3 }}>
        <DSTypography color="error">
          Failed to load contract: {error.message}
        </DSTypography>
      </DSBox>
    );
  }

  return (
    <DSBox sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DSTypography variant="h6">Studio Contract</DSTypography>
        <DSPrimaryButton
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
        >
          {isSaving ? "Saving..." : "Save Contract"}
        </DSPrimaryButton>
      </DSBox>
      {saveError && <DSTypography color="error">{saveError}</DSTypography>}
      <DSBox
        sx={{
          "& .ql-container": { minHeight: 400 },
          "& .ql-editor": { minHeight: 400 },
        }}
      >
        <ReactQuill theme="snow" value={html} onChange={handleChange} />
      </DSBox>
    </DSBox>
  );
};

export default StudioContractEditor;
